import { isPublicMarketingPath } from '@/lib/analytics/marketing-paths'
import { PLATFORM_HOST } from '@/lib/platform-host'

/**
 * Where Google's marketing tag (GA4 + Ads, one gtag.js) may load: the
 * marketing PATHS on the marketing HOST, outside the native shell. All three
 * must hold.
 *
 * - Path: lib/analytics/marketing-paths.ts — an allowlist, so the authenticated
 *   app never opts in by accident.
 * - Host: only the www/apex marketing host — surveythanks.com, plus whatever
 *   NEXT_PUBLIC_BASE_URL names (the same source lib/seo/site.ts derives the
 *   canonical host from; not imported from there because that module drags the
 *   OG media manifest into every client bundle). Partner
 *   custom domains (app.the90.com.au, app.orderjot.com, `<slug>.surveythanks.com`)
 *   serve the same routes — including `/` as a partner-branded landing page and
 *   `/email-entry` as their branded sign-in — and their visitors are a partner's
 *   customers, not our marketing funnel. Reporting them into our property would
 *   both pollute it and hand Google a white-label tenant's traffic.
 *   `localhost` is admitted so the tag can be exercised in local dev; it only
 *   ever loads there when a developer sets NEXT_PUBLIC_GA_MEASUREMENT_ID.
 * - Native shell: the iOS/Android app loads www (it must — the service worker
 *   is per-origin), so its sign-in screens are on the marketing host and path.
 *   They are still the app, not the website.
 *
 * Note the user_id consequence: GA never sees the authenticated app, so
 * `user_id` is attached on the sign-in/sign-up pages (which ARE on this
 * surface) and links a person's marketing sessions across devices — in-app
 * behaviour stays PostHog's job.
 */
function marketingHosts(baseUrl: string | undefined): Set<string> {
  const hosts = new Set([PLATFORM_HOST, `www.${PLATFORM_HOST}`])
  try {
    if (baseUrl) {
      const h = new URL(baseUrl).hostname.toLowerCase()
      hosts.add(h)
      hosts.add(h.replace(/^www\./, ''))
    }
  } catch {
    // A malformed base URL adds nothing; surveythanks.com still works.
  }
  return hosts
}

export function isMarketingTagHost(
  hostname: string | null | undefined,
  baseUrl: string | undefined = process.env.NEXT_PUBLIC_BASE_URL,
): boolean {
  if (!hostname) return false
  const h = hostname.trim().toLowerCase().replace(/\.$/, '')
  if (h === 'localhost' || h === '127.0.0.1') return true
  return marketingHosts(baseUrl).has(h)
}

export function shouldLoadMarketingTag(input: {
  pathname: string | null
  hostname: string | null
  nativeApp: boolean
}): boolean {
  if (input.nativeApp) return false
  if (!isMarketingTagHost(input.hostname)) return false
  return isPublicMarketingPath(input.pathname)
}
